import { Edit2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';

interface UnsavedChangesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDiscard: () => void;
  onSaveAndSwitch: () => void;
}

/**
 * Shown when the user clicks another block while the sidebar editor has
 * unsaved text. Lets them Save & Switch or Discard before moving on.
 */
export function UnsavedChangesDialog({
  open,
  onOpenChange,
  onDiscard,
  onSaveAndSwitch,
}: UnsavedChangesDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent hideClose className="!max-w-[320px] !p-0 !gap-0 overflow-hidden">
        <div className="px-5 pt-5 pb-4 text-center">
          <div className="w-10 h-10 rounded-full bg-[hsl(var(--warning))/0.12] flex items-center justify-center mx-auto mb-3">
            <Edit2 className="w-[18px] h-[18px] text-[hsl(var(--warning))]" />
          </div>
          <DialogHeader className="!p-0 !space-y-1 !text-center sm:!text-center">
            <DialogTitle className="text-[15px] font-semibold text-[hsl(var(--fg))]">
              {t('GENERATEREVIEW_UNSAVED_TITLE', { defaultValue: 'Unsaved changes' })}
            </DialogTitle>
            <DialogDescription className="text-[13px] leading-snug text-globe-grey">
              {t('GENERATEREVIEW_UNSAVED_BODY', {
                defaultValue: 'Save your edits or discard them to continue.',
              })}
            </DialogDescription>
          </DialogHeader>
        </div>
        <div className="flex border-t border-border">
          <button
            onClick={onDiscard}
            className="flex-1 py-2.5 text-[13px] font-medium text-globe-grey hover:bg-muted/60 active:bg-muted transition-colors border-r border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-selise-blue"
          >
            {t('GENERATEREVIEW_UNSAVED_DISCARD', { defaultValue: 'Discard' })}
          </button>
          <button
            autoFocus
            onClick={onSaveAndSwitch}
            className="flex-1 py-2.5 text-[13px] font-semibold text-selise-blue hover:bg-selise-blue/5 active:bg-selise-blue/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-selise-blue"
          >
            {t('GENERATEREVIEW_UNSAVED_SAVE', { defaultValue: 'Save & Switch' })}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
